import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/kimiko-kwinfinitidigital/30min";

const BookingModal = ({ open, onOpenChange }) => {
  const [step, setStep] = useState("form"); // "form" | "calendly"
  const [form, setForm] = useState({ name: "", email: "", company: "", goals: "" });
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep("form");
    setForm({ name: "", email: "", company: "", goals: "" });
    setSubmitting(false);
  };

  const handleOpenChange = (v) => {
    if (!v) {
      // small delay so modal fade-out doesn't flash reset
      setTimeout(reset, 200);
    }
    onOpenChange(v);
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.goals.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        goals: form.goals.trim(),
      });
      toast.success("Details saved. Pick a time that works for you.");
      setStep("calendly");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const calendlyEmbedUrl = () => {
    const params = new URLSearchParams({
      hide_gdpr_banner: "1",
      primary_color: "5B4B9A",
      name: form.name,
      email: form.email,
    });
    if (form.company) params.set("a1", form.company);
    return `${CALENDLY_URL}?${params.toString()}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="booking-modal-content max-w-[560px] sm:max-w-[560px] bg-[#FBF7FF] border-none rounded-2xl p-8"
      >
        {step === "form" ? (
          <>
            <DialogTitle asChild>
              <h3 data-testid="booking-modal-title">Book a free 30-minute strategy call</h3>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="sub">
                Tell me a bit about your business first — then pick a time that works for you.
              </p>
            </DialogDescription>
            <form className="booking-form" onSubmit={handleSubmit} data-testid="booking-form">
              <div className="booking-field">
                <label htmlFor="name">Your name <span className="req">*</span></label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jane Doe"
                  data-testid="booking-input-name"
                  required
                />
              </div>
              <div className="booking-field">
                <label htmlFor="email">Email <span className="req">*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jane@company.com"
                  data-testid="booking-input-email"
                  required
                />
              </div>
              <div className="booking-field">
                <label htmlFor="company">Company <span className="req">*</span></label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Acme Inc."
                  data-testid="booking-input-company"
                  required
                />
              </div>
              <div className="booking-field">
                <label htmlFor="goals">What are you trying to solve? <span className="req">*</span></label>
                <textarea
                  id="goals"
                  name="goals"
                  value={form.goals}
                  onChange={handleChange}
                  placeholder="e.g. Traffic isn't converting, launching a new product, need a content engine..."
                  data-testid="booking-input-goals"
                  required
                />
              </div>
              <button
                type="submit"
                className="booking-submit"
                disabled={submitting}
                data-testid="booking-submit-btn"
              >
                {submitting ? "Saving..." : "Continue to scheduling →"}
              </button>
            </form>
          </>
        ) : (
          <>
            <DialogTitle asChild>
              <h3 data-testid="calendly-step-title">Pick a time that works for you</h3>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="sub">Thanks, {form.name.split(" ")[0] || "there"}. Grab a slot below.</p>
            </DialogDescription>
            <div className="booking-success-banner" data-testid="booking-success-banner">
              Your details are saved — Kimiko will have context before the call.
            </div>
            <div className="calendly-wrapper" data-testid="calendly-embed">
              <iframe
                title="Schedule a call with Kimiko Williams"
                src={calendlyEmbedUrl()}
                allow="camera; microphone; autoplay; encrypted-media; fullscreen"
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Home = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="kw-root">
      <header className="nav">
        <div className="nav-inner">
          <div className="nav-name" data-testid="nav-name">
            KIMIKO WILLIAMS <span>· FRACTIONAL MARKETING</span>
          </div>
          <button
            className="nav-cta"
            onClick={openBooking}
            data-testid="nav-book-call-btn"
          >
            Book a call
          </button>
        </div>
      </header>

      <section className="hero">
        <div className="wrap hero-inner">
          <div>
            <span className="eyebrow">CDMP · 5+ years · founder-operator</span>
            <h1>
              Marketing leadership, without the <em>full-time hire.</em>
            </h1>
            <p className="lede">
              I step in as your fractional marketing lead, building the content, growth, and
              conversion strategy your brand needs, backed by campaigns that have driven{" "}
              <strong>290% boost in conversions</strong> and{" "}
              <strong>991% growth in visibility.</strong>
            </p>
            <div className="hero-ctas">
              <button
                className="btn-primary"
                onClick={openBooking}
                data-testid="hero-book-call-btn"
              >
                Book a free strategy call →
              </button>
              <a className="btn-secondary" href="#work" data-testid="hero-see-work-btn">
                See the work
              </a>
            </div>
            <p className="hero-note">30-minute call · just your goals and a plan</p>
          </div>
          <div className="hero-portrait">
            <img
              src="https://customer-assets.emergentagent.com/job_fractional-lead/artifacts/43uh1alg_unnamed.webp"
              alt="Kimiko Williams"
              data-testid="hero-portrait-img"
            />
            <div className="portrait-tag">
              <strong className="script" style={{ fontSize: "22px" }}>
                Kimiko Williams
              </strong>
              Founder, Blade Infiniti · Marketing Consultant, Infiniti Digital
            </div>
          </div>
        </div>
      </section>

      <div className="gradient-bar"></div>

      <section className="trust">
        <div className="wrap trust-inner">
          <span className="trust-label">As seen in</span>
          <span className="trust-item">Posh Lifestyle &amp; Beauty Blog</span>
          <span className="trust-item">Entreprenista</span>
          <span className="trust-item">BizWomen.com</span>
          <span className="trust-item">Oscars Week, Los Angeles</span>
          <span className="trust-item">New Media Film Festival, Los Angeles</span>
        </div>
      </section>

      <section className="stats" id="work">
        <div className="wrap">
          <div className="stats-head">
            <span className="kicker">Track record</span>
            <h2>Real campaigns, real numbers.</h2>
          </div>
          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-num">+290%</div>
              <div className="stat-label">
                Marketplace conversions from a $200 integrated campaign, B2B tech nonprofit
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-num">+991%</div>
              <div className="stat-label">Increase in visibility for a lifestyle brand</div>
            </div>
            <div className="stat-card">
              <div className="stat-num">88%</div>
              <div className="stat-label">
                Led event marketing and communications for a 5-region industry campaign — 88%
                attendee satisfaction, +7.5% YoY engagement, delivered 5.65% under budget, tech
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-num">75+</div>
              <div className="stat-label">
                Thought-leadership cover stories ghostwritten for executives, tech and media
              </div>
            </div>
          </div>
          <div className="ghost-credit" style={{ marginTop: "24px" }}>
            <p>
              Former copywriter and ghostwriter for <strong>Lynne Oldham</strong>, former Chief
              People Officer of Zoom, and the <strong>National Black Movie Association</strong>.
            </p>
          </div>
        </div>
      </section>

      <section className="press">
        <div className="wrap">
          <div className="press-head">
            <span className="kicker">Press &amp; PR wins</span>
            <h2>Earned media, not just paid reach.</h2>
          </div>
          <div className="press-feature-grid">
            <div className="press-feature">
              <div className="tag">Oscars Week, Los Angeles</div>
              <p>
                Secured a luxury celebrity gift bag placement with 135 best-selling hoodies
                distributed to actors, influencers, and entertainment personalities.
              </p>
            </div>
            <div className="press-feature">
              <div className="tag">New Media Film Festival, Los Angeles</div>
              <p>
                Landed product placements in gift bags distributed to judges from Marvel, HBO,
                BBC, PBS, the Emmy Awards, the Grammy Awards, and Simon &amp; Schuster.
              </p>
            </div>
          </div>
          <div className="press-mentions">
            <div className="tag">Press features</div>
            <ul>
              <li>
                Named one of the &ldquo;Female-Founded Brands You Need to Know&rdquo; by Posh
                Lifestyle &amp; Beauty Blog, March 2026
              </li>
              <li>
                Featured in BizWomen.com&apos;s Father&apos;s Day Round Up: Made For Men, Made
                By Women, June 2026
              </li>
              <li>
                Featured in Entreprenista&apos;s &ldquo;Season of Style&rdquo; fashion gift guide, Nov 2025
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="fit">
        <div className="wrap fit-grid">
          <div>
            <h2>This works if you&apos;re...</h2>
            <p className="sub">
              If you&apos;re a founder or you operate a lean team who needs senior marketing
              thinking without the headcount.
            </p>
          </div>
          <ul className="fit-list">
            <li>
              <span className="fit-num">01</span>
              <div>
                <strong>Growing but under-resourced</strong>
                <span>
                  You need a marketing lead&apos;s judgment, not another freelancer waiting on a
                  brief.
                </span>
              </div>
            </li>
            <li>
              <span className="fit-num">02</span>
              <div>
                <strong>Sitting on traffic that isn&apos;t converting</strong>
                <span>
                  Sessions are coming in but the funnel is leaking before checkout, signup, or
                  booking.
                </span>
              </div>
            </li>
            <li>
              <span className="fit-num">03</span>
              <div>
                <strong>Ready to build a content and brand engine</strong>
                <span>
                  Social, email, and storytelling that actually sound like your brand, not a
                  template.
                </span>
              </div>
            </li>
            <li>
              <span className="fit-num">04</span>
              <div>
                <strong>Launching or repositioning</strong>
                <span>
                  New product, new market, or a brand that needs a sharper story to tell.
                </span>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section className="process">
        <div className="wrap">
          <div className="process-head">
            <span className="kicker">How we&apos;d work together</span>
            <h2>Research to results, in four moves.</h2>
          </div>
          <div className="process-track">
            <div className="p-step">
              <div className="p-dot">01</div>
              <h3>Research</h3>
              <p>
                I dig into your business, audience, and funnel to understand exactly where the
                friction is.
              </p>
            </div>
            <div className="p-step">
              <div className="p-dot">02</div>
              <h3>Planning</h3>
              <p>
                Insights become a clear plan: goals, channels, timeline, and what success looks
                like.
              </p>
            </div>
            <div className="p-step">
              <div className="p-dot">03</div>
              <h3>Execution</h3>
              <p>Workflows get built, resources mobilized, and campaigns go live on schedule.</p>
            </div>
            <div className="p-step">
              <div className="p-dot">04</div>
              <h3>Analysis</h3>
              <p>Performance gets measured, gaps get flagged, and the next move gets sharper.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cases">
        <div className="wrap">
          <div className="cases-head">
            <span className="kicker">Case snapshots</span>
            <h2>Selected results.</h2>
          </div>
          <div className="case-grid">
            <div className="case-card">
              <div className="case-tag">Mark Chong&apos;s Cooking Mastery</div>
              <div className="case-num">+991%</div>
              <p>
                Redirected social content strategy to grow visibility by +991% and
                month-over-month engagement by +997%. Launched the brand&apos;s first email list
                via a trade show QR campaign — captured 53 signups with a 55% open rate and 4%
                CTR on the welcome email.
              </p>
            </div>
            <div className="case-card">
              <div className="case-tag">Jamaica Technology &amp; Digital Alliance</div>
              <div className="case-num">+290%</div>
              <p>
                Drove the Go Digital Marketplace campaign to a 290% lift in conversions on a $200
                media spend, and generated 100 marketing-qualified leads through a multi-touch
                email build.
              </p>
            </div>
            <div className="case-card">
              <div className="case-tag">Jamaicans.com</div>
              <div className="case-num">2x</div>
              <p>
                Refined content themes and publishing cadence to double post engagement in 3
                months, then ran a 7-creator campaign that generated the org&apos;s highest-ever
                survey response.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="testi">
        <div className="wrap">
          <div className="testi-head">
            <span className="kicker">What it&apos;s like to work with me</span>
            <h2>In their words.</h2>
          </div>
          <div className="testi-grid">
            <div className="testi-card">
              <p className="q">
                &ldquo;Kimiko is a FORCE to be reckoned with. Her goal-oriented and empathetic
                approach drove results for our organization while ensuring we fostered a fun and
                collaborative culture.&rdquo;
              </p>
              <p className="by">Vincent Brown, Former CEO of Influenced By Copy</p>
            </div>
            <div className="testi-card">
              <p className="q">
                &ldquo;I&apos;ve had the privilege of working with Kimiko, a multifaceted
                professional who strives for excellence and continuous improvement in all she
                does. I recommend her highly without hesitation.&rdquo;
              </p>
              <p className="by">Twitty-Ann Thomas, COO of Salus Technology Services</p>
            </div>
            <div className="testi-card">
              <p className="q">
                &ldquo;I really enjoyed Kimiko&apos;s energy and unwavering commitment to deliver
                excellence. She was a joy to work with and brought structure and synergy to our
                team.&rdquo;
              </p>
              <p className="by">
                Stacey Hines, Former President of Jamaica Technology and Digital Alliance
              </p>
            </div>
            <div className="testi-card">
              <p className="q">
                &ldquo;Kimiko&apos;s talent for organization and her ability to inspire has
                transformed our team dynamics, resulting in an efficient workflow and a culture
                of unwavering commitment to excellence.&rdquo;
              </p>
              <p className="by">Kareem James-King, Former Colleague, Jamaicans.com</p>
            </div>
          </div>
        </div>
      </section>

      <section className="creds">
        <div className="wrap creds-inner">
          <span className="cred-pill">CDMP · Digital Marketing Institute</span>
          <span className="cred-pill">ACIM · Chartered Institute of Marketing</span>
          <span className="cred-pill">Conversion Optimisation · CXL Institute</span>
          <span className="cred-pill">Applied Neuromarketing · CXL</span>
          <span className="cred-pill">Landing Page Optimization · CXL</span>
        </div>
      </section>

      <section className="cta-final" id="book">
        <div className="wrap">
          <h2>Let&apos;s find where your marketing is leaking, and fix it.</h2>
          <p>
            A free 30-minute call: I&apos;ll ask about your funnel, your goals, and where
            you&apos;re stuck, and tell you honestly if fractional support is the right fit.
          </p>
          <button
            className="btn-primary"
            onClick={openBooking}
            data-testid="cta-book-call-btn"
          >
            Book a free strategy call →
          </button>
          <div className="contact-row">
            <a href="mailto:kimiko@kwinfinitidigital.com" data-testid="contact-email">
              kimiko@kwinfinitidigital.com
            </a>
            <a href="tel:+18765801869" data-testid="contact-phone">
              (876) 580-1869
            </a>
            <a
              href="https://www.linkedin.com/in/kimiko-williams"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </section>

      <footer className="kw-footer">
        <div className="wrap">
          © 2026 Kimiko Williams. Founder &amp; Fractional Marketing Lead.
        </div>
      </footer>

      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
